import { createClient } from '@supabase/supabase-js'

// Estas variáveis vêm do arquivo .env.local
// Nunca commite o arquivo .env.local no GitHub!
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Variáveis REACT_APP_SUPABASE_URL e REACT_APP_SUPABASE_ANON_KEY não configuradas.')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// ─── HELPERS DE BANCO ─────────────────────────────────────────────────────────
// Converte snake_case do banco para camelCase do frontend
export const mapCliente = (c) => ({
  id: c.id,
  nome: c.nome,
  segmento: c.segmento,
  especialistaId: c.especialista_id,
  mentorId: c.mentor_id,
  tipoProjetoId: c.tipo_projeto_id || null,
  marcosDoProjetoIds: c.marcos_projeto || [],
  status: c.status,
  contrato: c.contrato,
  valor: c.valor,
  horasContratadas: c.horas_contratadas,
  telefone: c.telefone,
  email: c.email,
  cidade: c.cidade,
  clienteEmail: c.cliente_email,
  clienteSenha: c.cliente_senha,
})

export const mapEspecialista = (e) => ({
  id: e.id,
  nome: e.nome,
  email: e.email,
  senha: e.senha,
  perfil: e.perfil,
  especialidade: e.especialidade,
  status: e.status,
  telefone: e.telefone,
  bio: e.bio,
  tipoAtuacao: e.tipo_atuacao || [],
})

export const mapVisita = (v) => ({
  id: v.id,
  clienteId: v.cliente_id,
  especialistaId: v.especialista_id,
  data: v.data,
  tipo: v.tipo,
  tipoAgenda: v.tipo_agenda,
  duracao: v.duracao,
  descricao: v.descricao,
  ata: v.ata,
  status: v.status,
  ganhos: v.ganhos || [],
  tarefasGeradas: v.tarefas_geradas || [],
  itensPauta: v.itens_pauta || [],
  anexos: v.anexos || [],
  horarios: v.horarios || [],
  consultorId: v.consultor_id || null,
  despesaViagem: v.despesa_viagem || [],
  confirmadoCliente: v.confirmado_cliente || false,
})

export const mapMarco = (m) => ({
  id: m.id,
  titulo: m.titulo,
  descricao: m.descricao,
  tipoMarco: m.tipo_marco || "Global",
  tipoProjetoId: m.tipo_projeto_id || null,
  prioridade: m.prioridade || "Média",
})

export const mapTipoProjeto = (t) => ({
  id: t.id,
  tipo: t.tipo,
  descricao: t.descricao || "",
})

export const mapTarefa = (t) => ({
  id: t.id,
  clienteId: t.cliente_id,
  visitaId: t.visita_id,
  titulo: t.titulo,
  responsavel: t.responsavel,
  prazo: t.prazo,
  prioridade: t.prioridade,
  status: t.status,
  obs: t.obs,
  criadaEm: t.criada_em,
  concluidaEm: t.concluida_em,
})

export const mapDps = (d) => ({
  id: d.id,
  clienteId: d.cliente_id,
  setor: d.setor,
  data: d.data,
  especialistaId: d.especialista_id || null,
  consultorId: d.consultor_id || null,
  pilares: d.pilares || {},
  obs: d.obs || '',
})

export const mapDmg = (d) => ({
  clienteId: d.cliente_id,
  lideranca: d.lideranca,
  estrategia: d.estrategia,
  clientes: d.clientes_pilar,
  sociedade: d.sociedade,
  informacao: d.informacao,
  pessoas: d.pessoas,
  processos: d.processos,
  analiseIA: d.analise_ia || null,
})

// ─── API FUNCTIONS ─────────────────────────────────────────────────────────────

// ESPECIALISTAS
export const api = {
  especialistas: {
    list: async () => {
      const { data, error } = await supabase.from('especialistas').select('*').order('nome')
      if (error) throw error
      return data.map(mapEspecialista)
    },
    upsert: async (esp) => {
      const row = {
        nome: esp.nome, email: esp.email, senha: esp.senha,
        perfil: esp.perfil, especialidade: esp.especialidade,
        status: esp.status, telefone: esp.telefone, bio: esp.bio,
        tipo_atuacao: esp.tipoAtuacao || [],
      }
      if (esp.id) row.id = esp.id
      const { data, error } = await supabase.from('especialistas').upsert(row).select().single()
      if (error) throw error
      return mapEspecialista(data)
    },
  },

  clientes: {
    list: async () => {
      const { data, error } = await supabase.from('clientes').select('*').order('nome')
      if (error) throw error
      return data.map(mapCliente)
    },
    upsert: async (c) => {
      const row = {
        nome: c.nome, segmento: c.segmento, especialista_id: c.especialistaId || null,
        mentor_id: c.mentorId || null,
        tipo_projeto_id: c.tipoProjetoId || null,
        marcos_projeto: c.marcosDoProjetoIds || [],
        status: c.status, contrato: c.contrato || null, valor: c.valor || null,
        horas_contratadas: c.horasContratadas || null,
        telefone: c.telefone, email: c.email, cidade: c.cidade,
        cliente_email: c.clienteEmail || null,
        cliente_senha: c.clienteSenha || null,
      }
      if (c.id) row.id = c.id
      const { data, error } = await supabase.from('clientes').upsert(row).select().single()
      if (error) throw error
      return mapCliente(data)
    },
    delete: async (id) => {
      const { error } = await supabase.from('clientes').delete().eq('id', id)
      if (error) throw error
    },
  },

  visitas: {
    list: async () => {
      const { data, error } = await supabase.from('visitas').select('*').order('data', { ascending: false })
      if (error) throw error
      return data.map(mapVisita)
    },
    upsert: async (v) => {
      const row = {
        cliente_id: v.clienteId, especialista_id: v.especialistaId,
        data: v.data, tipo: v.tipo, tipo_agenda: v.tipoAgenda,
        duracao: v.duracao, descricao: v.descricao, ata: v.ata,
        status: v.status,
        ganhos: v.ganhos || [],
        tarefas_geradas: v.tarefasGeradas || [],
        itens_pauta: v.itensPauta || [],
        anexos: v.anexos || [],
        horarios: v.horarios || [],
        consultor_id: v.consultorId || null,
        despesa_viagem: v.despesaViagem || [],
        confirmado_cliente: v.confirmadoCliente || false,
      }
      if (v.id) row.id = v.id
      const { data, error } = await supabase.from('visitas').upsert(row).select().single()
      if (error) throw error
      return mapVisita(data)
    },
    delete: async (id) => {
      const { error } = await supabase.from('visitas').delete().eq('id', id)
      if (error) throw error
    },
  },

  marcos: {
    list: async () => {
      const { data, error } = await supabase.from('marcos').select('*').order('titulo')
      if (error) throw error
      return data.map(mapMarco)
    },
    upsert: async (m) => {
      const row = {
        titulo: m.titulo, descricao: m.descricao,
        tipo_marco: m.tipoMarco || "Global",
        tipo_projeto_id: m.tipoProjetoId || null,
        prioridade: m.prioridade || "Média",
      }
      if (m.id) row.id = m.id
      const { data, error } = await supabase.from('marcos').upsert(row).select().single()
      if (error) throw error
      return mapMarco(data)
    },
  },

  tiposProjeto: {
    list: async () => {
      const { data, error } = await supabase.from('tipos_projeto').select('*').order('tipo')
      if (error) throw error
      return data.map(mapTipoProjeto)
    },
    upsert: async (t) => {
      const row = { tipo: t.tipo, descricao: t.descricao || "" }
      if (t.id) row.id = t.id
      const { data, error } = await supabase.from('tipos_projeto').upsert(row).select().single()
      if (error) throw error
      return mapTipoProjeto(data)
    },
    delete: async (id) => {
      const { error } = await supabase.from('tipos_projeto').delete().eq('id', id)
      if (error) throw error
    },
  },

  tarefas: {
    list: async () => {
      const { data, error } = await supabase.from('tarefas').select('*').order('prazo')
      if (error) throw error
      return data.map(mapTarefa)
    },
    upsert: async (t) => {
      const row = {
        cliente_id: t.clienteId, visita_id: t.visitaId || null,
        titulo: t.titulo, responsavel: t.responsavel,
        prazo: t.prazo || null, prioridade: t.prioridade,
        status: t.status, obs: t.obs,
        criada_em: t.criadaEm || new Date().toISOString().split('T')[0],
        concluida_em: t.concluidaEm || null,
      }
      if (t.id) row.id = t.id
      const { data, error } = await supabase.from('tarefas').upsert(row).select().single()
      if (error) throw error
      return mapTarefa(data)
    },
    delete: async (id) => {
      const { error } = await supabase.from('tarefas').delete().eq('id', id)
      if (error) throw error
    },
    updateStatus: async (id, status) => {
      const row = { status, concluida_em: status === 'Concluída' ? new Date().toISOString().split('T')[0] : null }
      const { error } = await supabase.from('tarefas').update(row).eq('id', id)
      if (error) throw error
    },
  },

  dmg: {
    list: async () => {
      const { data, error } = await supabase.from('dmg').select('*')
      if (error) throw error
      const result = {}
      data.forEach(d => { result[d.cliente_id] = mapDmg(d) })
      return result
    },
    upsert: async (clienteId, scores, analiseIA) => {
      const row = {
        cliente_id: clienteId,
        lideranca: scores.lideranca, estrategia: scores.estrategia,
        clientes_pilar: scores.clientes, sociedade: scores.sociedade,
        informacao: scores.informacao, pessoas: scores.pessoas,
        processos: scores.processos, updated_at: new Date().toISOString(),
        analise_ia: analiseIA || null,
      }
      const { error } = await supabase.from('dmg').upsert(row, { onConflict: 'cliente_id' })
      if (error) throw error
    },
    delete: async (clienteId) => {
      const { error } = await supabase.from('dmg').delete().eq('cliente_id', clienteId)
      if (error) throw error
    },
  },

  comentarios: {
    list: async (clienteId) => {
      const { data, error } = await supabase.from('comentarios').select('*').eq('cliente_id', clienteId).order('created_at')
      if (error) throw error
      return data.map(c => ({ id: c.id, clienteId: c.cliente_id, tarefaId: c.tarefa_id, autor: c.autor, texto: c.texto, criadoEm: c.created_at }))
    },
    insert: async (c) => {
      const row = { cliente_id: c.clienteId, tarefa_id: c.tarefaId, autor: c.autor, texto: c.texto }
      const { data, error } = await supabase.from('comentarios').insert(row).select().single()
      if (error) throw error
      return { id: data.id, clienteId: data.cliente_id, tarefaId: data.tarefa_id, autor: data.autor, texto: data.texto, criadoEm: data.created_at }
    },
  },

  consultores: {
    list: async () => {
      const { data, error } = await supabase.from('consultores').select('*').order('nome')
      if (error) throw error
      return data.map(c => ({
        id: c.id, nome: c.nome, email: c.email || '', telefone: c.telefone || '',
        especialidade: c.especialidade || '', status: c.status || 'Ativo',
        valorHoraAtendimento: c.valor_hora_atendimento || 0,
        valorHoraDeslocamento: c.valor_hora_deslocamento || 0,
        bio: c.bio || '',
        consultorEmail: c.consultor_email || '',
        consultorSenha: c.consultor_senha || '',
      }))
    },
    login: async (email, senha) => {
      const { data, error } = await supabase.from('consultores')
        .select('*').eq('consultor_email', email).eq('consultor_senha', senha).single()
      if (error || !data) return null
      return {
        id: data.id, nome: data.nome, email: data.email || '',
        especialidade: data.especialidade || '', status: data.status || 'Ativo',
        valorHoraAtendimento: data.valor_hora_atendimento || 0,
        valorHoraDeslocamento: data.valor_hora_deslocamento || 0,
        bio: data.bio || '', perfil: 'Consultor',
        consultorEmail: data.consultor_email || '',
      }
    },
    upsert: async (c) => {
      const row = {
        nome: c.nome, email: c.email || '', telefone: c.telefone || '',
        especialidade: c.especialidade || '', status: c.status || 'Ativo',
        valor_hora_atendimento: c.valorHoraAtendimento || 0,
        valor_hora_deslocamento: c.valorHoraDeslocamento || 0,
        bio: c.bio || '',
        consultor_email: c.consultorEmail || null,
        consultor_senha: c.consultorSenha || null,
      }
      if (c.id) row.id = c.id
      const { data, error } = await supabase.from('consultores').upsert(row).select().single()
      if (error) throw error
      return {
        id: data.id, nome: data.nome, email: data.email || '', telefone: data.telefone || '',
        especialidade: data.especialidade || '', status: data.status || 'Ativo',
        valorHoraAtendimento: data.valor_hora_atendimento || 0,
        valorHoraDeslocamento: data.valor_hora_deslocamento || 0,
        bio: data.bio || '',
        consultorEmail: data.consultor_email || '',
        consultorSenha: data.consultor_senha || '',
      }
    },
    delete: async (id) => {
      const { error } = await supabase.from('consultores').delete().eq('id', id)
      if (error) throw error
    },
  },

  dps: {
    list: async () => {
      const { data, error } = await supabase.from('dps_aplicacoes').select('*').order('data', { ascending: false })
      if (error) throw error
      return data.map(mapDps)
    },
    upsert: async (d) => {
      const row = {
        cliente_id: d.clienteId,
        setor: d.setor,
        data: d.data,
        especialista_id: d.especialistaId || null,
        consultor_id: d.consultorId || null,
        pilares: d.pilares || {},
        obs: d.obs || '',
      }
      if (d.id) row.id = d.id
      const { data: saved, error } = await supabase.from('dps_aplicacoes').upsert(row).select().single()
      if (error) throw error
      return mapDps(saved)
    },
    delete: async (id) => {
      const { error } = await supabase.from('dps_aplicacoes').delete().eq('id', id)
      if (error) throw error
    },
  },

  prospects: {
    list: async () => {
      const { data, error } = await supabase.from('prospects').select('*').order('criado_em', { ascending: false })
      if (error) throw error
      return data.map(p => ({
        id: p.id,
        nome: p.nome,
        responsavel: p.responsavel || '',
        segmento: p.segmento || '',
        telefone: p.telefone || '',
        cidade: p.cidade || '',
        emailResponsavel: p.email_responsavel || '',
        obs: p.obs || '',
        fase: p.fase || 'CONTRATANDO',
        checklist: p.checklist || {},
        criadoEm: p.criado_em,
      }))
    },
    upsert: async (p) => {
      const row = {
        nome: p.nome,
        responsavel: p.responsavel || '',
        segmento: p.segmento || '',
        telefone: p.telefone || '',
        cidade: p.cidade || '',
        email_responsavel: p.emailResponsavel || '',
        obs: p.obs || '',
        fase: p.fase || 'CONTRATANDO',
        checklist: p.checklist || {},
        updated_at: new Date().toISOString(),
      }
      if (p.id && !p.id.startsWith('p_')) row.id = p.id
      const { data, error } = await supabase.from('prospects').upsert(row).select().single()
      if (error) throw error
      return {
        id: data.id, nome: data.nome, responsavel: data.responsavel || '',
        segmento: data.segmento || '', telefone: data.telefone || '',
        cidade: data.cidade || '', emailResponsavel: data.email_responsavel || '',
        obs: data.obs || '', fase: data.fase || 'CONTRATANDO',
        checklist: data.checklist || {}, criadoEm: data.criado_em,
      }
    },
    delete: async (id) => {
      const { error } = await supabase.from('prospects').delete().eq('id', id)
      if (error) throw error
    },
  },
}
